import type {UserMediaType, UploadFileDataTypes, UserMediaVOResponse} from '@Libraries/user-media-library';
import {uploadFiles} from '@Libraries/user-media-library';
import {isTempImageData} from '@Libraries/add-media-library';
import {checkUserImagePresets} from '@Libraries/ai-chat.library';
import {getUserId} from '@Libraries/user.library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import type {PromptAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {
  convertUserMediaVOToAttachment,
  LoadingAttachmentsTracker,
  MAX_ATTACHMENTS,
} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import isArray from 'lodash/isArray';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES = ACCEPTED_IMAGE_TYPES;

export interface DragDropUploadState {
  getAttachmentCount: () => number;
  addLoadingAttachments: (count: number) => string[];
  removeLoadingAttachments: (ids: string[]) => void;
  addAttachments: (attachments: PromptAttachment[]) => void;
  setActive: () => void;
}

export const handleDragDropFilesUpload = (files: File[], stateAccessors: DragDropUploadState, onInvalidFileTypeError?: VoidFunction): void => {
  const currentAttachmentCount = stateAccessors.getAttachmentCount();
  if (currentAttachmentCount >= MAX_ATTACHMENTS) {
    return;
  }

  const imageFiles = files.filter((file) => ACCEPTED_IMAGE_TYPES.includes(file.type));
  if (imageFiles.length === 0) {
    if (files.length > 0) {
      onInvalidFileTypeError?.();
    }
    return;
  }

  const availableSlots = MAX_ATTACHMENTS - currentAttachmentCount;
  const filesToUpload = imageFiles.slice(0, availableSlots);

  const tracker = new LoadingAttachmentsTracker();
  const loadingIds = stateAccessors.addLoadingAttachments(filesToUpload.length);
  tracker.addIds(loadingIds);

  stateAccessors.setActive();

  let validationChain = Promise.resolve();

  void uploadFiles(
    {
      onFileUpload: (_type: UserMediaType, data: UploadFileDataTypes): void => {
        const responses = extractNonTempResponses(data);
        if (responses.length > 0) {
          validationChain = validationChain.then(() => validateAndAddAttachments(responses, tracker, stateAccessors));
        }
      },
      onUploadError: (): void => {
        const {idsToRemove} = tracker.resolveIds(1);
        stateAccessors.removeLoadingAttachments(idsToRemove);
      },
    },
    filesToUpload,
    {shouldLoadDataUrlImage: false}
  );
};

const extractNonTempResponses = (data: UploadFileDataTypes): UserMediaVOResponse[] => {
  if (isArray(data)) {
    return data.filter((item): item is UserMediaVOResponse => !isTempImageData(item));
  }

  if (!isTempImageData(data)) {
    return [data];
  }

  return [];
};

const validateAndAddAttachments = async (responses: UserMediaVOResponse[], tracker: LoadingAttachmentsTracker, stateAccessors: DragDropUploadState): Promise<void> => {
  const attachments = responses.map((response) => convertUserMediaVOToAttachment(response, PromptAttachmentType.IMAGE));

  if (!getUserId()) {
    const {idsToRemove, activeIds} = tracker.resolveIds(responses.length);
    stateAccessors.removeLoadingAttachments(idsToRemove);

    if (activeIds.length > 0) {
      stateAccessors.addAttachments(attachments.slice(0, activeIds.length));
    }

    return;
  }

  const hashedIds = attachments.map((attachment) => attachment.hashedFilename);

  try {
    const validationResults = await checkUserImagePresets(hashedIds);
    const validAttachments = attachments.filter((attachment) => validationResults[attachment.hashedFilename]);

    if (validAttachments.length < attachments.length) {
      const invalidCount = attachments.length - validAttachments.length;
      const errorKey = invalidCount > 1 ? 'pmwjs_error_processing_some_images' : 'pmwjs_error_processing_some_image';

      showMessageGrowl({
        type: GROWL_TYPE.DANGER,
        text: window.i18next.t(errorKey),
        showCloseIcon: true,
        interval: 7000,
      });
    }

    const {idsToRemove, activeIds} = tracker.resolveIds(responses.length);
    stateAccessors.removeLoadingAttachments(idsToRemove);

    if (activeIds.length > 0) {
      stateAccessors.addAttachments(validAttachments.slice(0, activeIds.length));
    }
  } catch {
    showMessageGrowl({
      type: GROWL_TYPE.DANGER,
      text: window.i18next.t('pmwjs_error_processing_all_images'),
      showCloseIcon: true,
      interval: 7000,
    });

    stateAccessors.removeLoadingAttachments(tracker.resolveAllOnError());
  }
};
